// src/oura/newscan/fixtures/formCards.ts
//
// The rest of the client's form in the First AI check Review (handoffs H3-b
// and H5-b). parse_answers (BE dependencies/oura_onboarding.py:1113) files
// eight fields; services and outlet areas were already cards (firstCheck.ts).
// YW, 15 Oct: staff decide these by hand, and Oura recommends nothing.
//
//   customerQuestions  every question the client's customers ask that no row
//                      asks, as optional offers in one quiet fold: each lists
//                      the rows of its group it could replace, and staff pick
//                      one ("Swap in"). A swap is a lint-gated reword. Never a
//                      decision: never counted, never holds Approve or Start.
//   goals              only a goal that clearly names a service the business
//                      offers (in the 50 or on the form) raises one card,
//                      non-blocking, with two equal choices. A vague goal
//                      raises nothing and is shown as context.
//   plans, other       context only, in the client's words (Review's "From
//                      the form" block). Plans never fill the promotion row.
//
// Pure: the store keeps the state.

import type { ScanDraftBusiness, ScanDraftDecision } from "@service/aeo/scanDraftTypes";
import type { AssembledRow, FirstCheckFacts } from "./model";
import type { LintIssue } from "./lint";
import type { WordSource } from "./words";
import { familyMembers } from "./recipe";

const norm = (s: string) => s.trim().toLowerCase().replace(/[‘’']/g, "").replace(/[-–]/g, " ").replace(/\s+/g, " ");
const slugOf = (s: string) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
const cap = (s: string) => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s);
const lower = (s: string) => (s ? `${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);

/** What a card needs from the draft. */
export interface FormCardInput {
    facts: FirstCheckFacts;
    business: ScanDraftBusiness;
    rows: AssembledRow[];
    src: WordSource;
    issues: LintIssue[];
    /** "Sarah": the form's contact, first name. */
    who: string;
    /** "From Sarah's form, 15 Oct". */
    origin: string;
    answered: Record<string, "dismissed" | "acked" | "resolved">;
}

/* ------------------------------------------------------------------------- *
 * Services the form and the 50 name
 * ------------------------------------------------------------------------- */

/** The distinctive words of a service: "teeth whitening" -> ["whitening"], "Invisalign" -> ["invisalign"]. */
const GENERIC = new Set(["teeth", "tooth", "dental", "treatment", "service", "services", "care", "therapy", "session", "class", "classes", "and", "for", "the", "with"]);
function keyWords(service: string): string[] {
    const words = norm(service).split(" ").filter((w) => w.length >= 4 && !GENERIC.has(w));
    return words.length ? words : norm(service).split(" ").filter(Boolean);
}

/** The services this business is known for: the form's, the business's and the service words the 50 use. */
function knownServices(input: FormCardInput): string[] {
    const fromWords = ["core_service", ...familyMembers("core_service")].map((v) => input.src.value(v)).filter(Boolean);
    const all = [...input.facts.formServices, ...input.business.services.map((s) => s.name), ...fromWords];
    const seen = new Set<string>();
    return all.filter((s) => s.trim() && !seen.has(norm(s)) && Boolean(seen.add(norm(s))));
}

/** The first known service a sentence names, as the business spells it (lower case). */
function serviceIn(text: string, services: string[]): string | null {
    // Punctuation is not a word: "How much is Invisalign?" names Invisalign.
    const t = ` ${norm(text).replace(/[^\p{L}\p{N}\s]/gu, " ").replace(/\s+/g, " ")} `;
    const hit = services.find((s) => keyWords(s).some((w) => t.includes(` ${w} `) || t.includes(` ${w}s `)));
    return hit ? asWritten(hit.trim(), text) : null;
}

/**
 * A service as a sentence carries it: a name the client writes with a capital
 * mid-sentence keeps it ("Invisalign"), a common one reads in lower case
 * ("teeth whitening").
 */
function asWritten(service: string, text: string): string {
    const word = service.split(/\s+/)[0] ?? "";
    const at = text.search(new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i"));
    const proper = at > 0 && /[A-Z]/.test(text.charAt(at)) && /[A-Z]/.test(service.charAt(0));
    return proper ? service : lower(service);
}

/* ------------------------------------------------------------------------- *
 * Customer questions
 * ------------------------------------------------------------------------- */

/** A customer question in the recipe's own shape: the group its row comes from, and the words that show a row asks it. */
interface CustomerShape {
    group: string;
    text: (place: string, city: string) => string;
    /** Every one must be in a row's wording for that row to count as asking it. */
    terms: string[];
}

/**
 * The recipe shape for a customer's question, or null when the recipe has
 * none (a how-long or a how-to question: the engines answer those without
 * naming a business, the dead shapes rule).
 */
export function customerShape(question: string, services: string[]): CustomerShape | null {
    const t = norm(question);
    const svc = serviceIn(question, services);
    const svcKey = svc ? keyWords(svc)[0] : null;
    if (/\b(instal?ments?|installments?|pay monthly|payment plan|easy payment)\b/.test(t)) {
        return {
            group: "price",
            text: (place, city) => (svc ? `Which ${place} in ${city} offers ${svc} by instalments?` : `Which ${place} in ${city} lets you pay by instalments?`),
            terms: ["instalment"],
        };
    }
    if (/\b(insurance|panel clinic|panel|claim)\b/.test(t)) {
        return { group: "price", text: (place, city) => `Which ${place} in ${city} accepts insurance?`, terms: ["insurance"] };
    }
    if (/\b(how much|price|prices|cost|costs|fee|fees|harga|charge)\b/.test(t)) {
        if (!svc) return { group: "price", text: (place, city) => `How much does a ${place} in ${city} charge?`, terms: ["cost|price|how much|charge"] };
        return { group: "price", text: (_place, city) => `How much does ${svc} cost in ${city}?`, terms: [svcKey ?? svc, "cost|price|how much|charge"] };
    }
    if (/\b(pain|painful|hurt|hurts|sakit)\b/.test(t)) {
        if (!svc) return { group: "audience", text: (place, city) => `Which ${place} in ${city} is good for people afraid of pain?`, terms: ["pain"] };
        return { group: "audience", text: (_place, city) => `Where can I get pain-free ${svc} in ${city}?`, terms: [svcKey ?? svc, "pain"] };
    }
    if (/\bwalk ?ins?\b/.test(t)) {
        return { group: "booking", text: (place, city) => `Which ${place} in ${city} takes walk-ins without an appointment?`, terms: ["walk in"] };
    }
    if (/\bpark(ing)?\b/.test(t)) {
        return { group: "differentiator", text: (place, city) => `Which ${place} in ${city} has easy parking?`, terms: ["parking"] };
    }
    const when = /\bopen\b.*?\b(on sundays?|on saturdays?|on weekends?|at weekends?|sundays?|saturdays?|weekends?|on public holidays|public holidays|late|at night|after \d+ ?(?:pm)?)\b/.exec(t);
    if (when) {
        const day = when[1].replace(/^(on|at) /, "").replace(/s$/, "");
        // Days and holidays keep their capitals in the question ("on Saturday", "on Public holidays" never).
        const said = /^(sunday|saturday)$/.test(day) ? `${day.charAt(0).toUpperCase()}${day.slice(1)}` : day === "public holiday" ? "public holidays" : day;
        const phrase = /^(late|night|after)/.test(day) ? (day === "night" ? "at night" : day) : `on ${said}`;
        return { group: "situational", text: (place, city) => `Which ${place} in ${city} is open ${phrase}?`, terms: ["open", day.split(" ")[0]] };
    }
    const who = /\b(kids?|children|child|toddlers?|seniors?|elderly|pregnant)\b/.exec(t);
    if (who) {
        const word = /^(kid|kids|child|children|toddler|toddlers)$/.test(who[1]) ? "children" : who[1] === "elderly" || who[1].startsWith("senior") ? "seniors" : "pregnant women";
        return { group: "audience", text: (place, city) => `Which ${place} in ${city} is good for ${word}?`, terms: [word === "pregnant women" ? "pregnan" : word.replace(/s$/, "")] };
    }
    return null;
}

/** A row asks the customer's question when its wording holds every term (a term may list choices with |). */
function rowAsks(row: AssembledRow, terms: string[]): boolean {
    const text = ` ${norm(row.question.text).replace(/installment/g, "instalment")} `;
    return terms.every((term) => term.split("|").some((choice) => text.includes(norm(choice))));
}

/** City-level rows of a group that a swap may take: never a location row, a locked row or a language row. */
function swappable(rows: AssembledRow[], group: string): AssembledRow[] {
    return rows.filter((r) => r.plan.group === group && r.plan.check !== "location" && !r.question.locked && r.plan.language === "en");
}

/**
 * The weakest row of a group, by the lint's own measures: the most lint
 * findings first, then a row whose subject is the profession word (the
 * category cap the recipe keeps down), then the latest slot.
 */
function weakest(candidates: AssembledRow[], issues: LintIssue[], place: string, taken: Set<number>): AssembledRow | null {
    const findings = (r: AssembledRow) => issues.filter((i) => i.n === r.question.n).length;
    const generic = (r: AssembledRow) => (place && norm(r.question.text).includes(norm(place)) ? 1 : 0);
    return (
        candidates
            .filter((r) => !taken.has(r.question.n))
            .sort((a, b) => findings(b) - findings(a) || generic(b) - generic(a) || b.question.n - a.question.n)[0] ?? null
    );
}

const GROUP_WORDS: Record<string, string> = {
    price: "price",
    audience: "who it is for",
    booking: "booking",
    differentiator: "what sets them apart",
    situational: "opening hours",
};

/** A customer question no row asks, with the rows of its group it could replace (weakest first). */
export interface CustomerQuestionOffer {
    question: string;
    /** The recipe group it belongs to ("price"), and that group in words. Null: no recipe shape fits. */
    group: string | null;
    groupLabel: string | null;
    /** Each row it may replace, with the wording it would then have (that row's own town). */
    options: Array<{ n: number; current: string; text: string }>;
}

/**
 * Every customer question no row asks (YW, 15 Oct: staff decide by hand, so
 * Oura recommends nothing). Each lists the rows of its group it could
 * replace, the weakest first by the lint's measures, never a wording the 50
 * already hold. A swap is a reword of that row, lint-gated like any other.
 */
export function customerQuestionOffers(input: Pick<FormCardInput, "facts" | "business" | "rows" | "src" | "issues">): CustomerQuestionOffer[] {
    const services = knownServices(input as FormCardInput);
    const place = input.src.value("place_noun") || input.business.category.toLowerCase();
    const texts = new Set(input.rows.map((r) => norm(r.question.text)));
    const seen = new Set<string>();
    const out: CustomerQuestionOffer[] = [];
    input.facts.customerQuestions
        .map((x) => x.trim())
        .filter((x) => x && !seen.has(norm(x)) && Boolean(seen.add(norm(x))))
        .forEach((question) => {
            const shape = customerShape(question, services);
            if (shape && input.rows.some((r) => rowAsks(r, shape.terms))) return;
            if (!shape) {
                out.push({ question, group: null, groupLabel: null, options: [] });
                return;
            }
            const ranked: AssembledRow[] = [];
            const pool = swappable(input.rows, shape.group);
            let next = weakest(pool, input.issues, place, new Set());
            while (next) {
                ranked.push(next);
                next = weakest(pool, input.issues, place, new Set(ranked.map((r) => r.question.n)));
            }
            const options = ranked
                .map((r) => ({ n: r.question.n, current: r.question.text, text: shape.text(place, r.plan.town ?? input.business.city) }))
                .filter((o) => !texts.has(norm(o.text)));
            out.push({ question, group: shape.group, groupLabel: GROUP_WORDS[shape.group] ?? shape.group, options });
        });
    return out;
}

/* ------------------------------------------------------------------------- *
 * Goals: the main service
 * ------------------------------------------------------------------------- */

/** The rows a core swap rewords: the main-service rows, and the rows of the word the new one held. */
export function coreSwap(rows: AssembledRow[], src: WordSource, name: string): { twin: string | null; rows: number[] } {
    // The word that holds this very service first: a looser match ("acne" in "laser acne scar treatment")
    // picked the wrong word on a swap back, and the reverse swap left questions repeating each other.
    // Only the words these 50 use: the family also lists brand-mode words (core_product), which a
    // local draft never asks, and swapping into one of those changed nothing on the way back.
    const used = new Set(rows.flatMap((r) => r.question.fill_vars));
    const members = familyMembers("core_service").filter((v) => used.has(v));
    const twin =
        members.find((v) => v !== "core_service" && norm(src.value(v)) === norm(name)) ??
        members.find((v) => v !== "core_service" && keyWords(name).some((w) => norm(src.value(v)).includes(w))) ??
        null;
    const uses = rows.filter((r) => !r.question.locked && (r.question.fill_vars.includes("core_service") || (twin !== null && r.question.fill_vars.includes(twin))));
    return { twin, rows: uses.map((r) => r.question.n) };
}

function goalCards(input: FormCardInput): ScanDraftDecision[] {
    const goals = (input.facts.goals ?? []).map((g) => g.trim()).filter(Boolean);
    const core = input.src.value("core_service");
    if (!core || !goals.length) return [];
    const services = knownServices(input);
    for (const goal of goals) {
        const svc = serviceIn(goal, services);
        if (!svc || keyWords(svc).some((w) => norm(core).includes(w))) continue;
        const swap = coreSwap(input.rows, input.src, svc);
        if (!swap.rows.length) continue;
        const k = swap.rows.length;
        return [
            card(`form_goal_core:${slugOf(svc)}`, "suggestion", {
                title: `${input.who}'s goal: ${lower(goal.replace(/\.$/, ""))}. Make ${cap(svc)} a core service?`,
                detail: `It would change ${k} ${k === 1 ? "question" : "questions"}: the core service questions ask about ${svc} in place of ${core}${swap.twin ? `, and ${core} takes the place ${svc} had` : ""}. Nothing changes unless you choose it.`,
                slots: swap.rows,
                origin: input.origin,
                // Two equal choices (YW, 15 Oct): Oura recommends neither.
                recommended: null,
                alternatives: [
                    { label: `Make ${svc} core`, action: { kind: "set_core_service", name: svc } },
                    { label: `Keep ${core}`, action: { kind: "dismiss" } },
                ],
            }),
        ];
    }
    return [];
}

/* ------------------------------------------------------------------------- *
 * The cards, in the Review's order
 * ------------------------------------------------------------------------- */

function card(id: string, severity: ScanDraftDecision["severity"], fields: Partial<ScanDraftDecision> & { title: string }): ScanDraftDecision {
    const type = id.split(":")[0] as ScanDraftDecision["type"];
    return {
        id,
        type,
        severity,
        detail: null,
        quotes: [],
        slots: [],
        state: "open",
        recommended: null,
        alternatives: [],
        evidence: null,
        ...fields,
    };
}

/** The one goal card, when a goal clearly names a service the business offers. */
export function formAnswerDecisions(input: FormCardInput): ScanDraftDecision[] {
    return goalCards(input);
}
